import React, { useContext, useEffect, useReducer } from 'react'
import { ActionChip, Button, DataBlock, InputGroup, Row, Radio, RadioGroup, TextField } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { deepCopy } from '../../../util/Utils'
import { createAsMember, deleteAsMember, getAsMember } from '../../../services/membershipService'
import useNotifications from '../../../hooks/useNotifications'
import { DmcRow } from '../../common/DmcRow'
import { UserLink } from '../../app/Links'
import { PermissionsContext } from '../../app/PermissionsContext'

export default () => {}

// View
export const radioButtons = {
  YES: 'Yes',
  NO: 'No',
}

export function TreatAsMember({ cisId }) {
  const { isAuthorized } = useContext(PermissionsContext)
  const { treatAsMemberState, treatAsMemberActions } = useTreatAsMemberController(cisId, isAuthorized)

  useEffect(() => {
    treatAsMemberActions.loadData()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  if (!isAuthorized(['ViewTreatAsMember']) || treatAsMemberState.treatAsMember == null) {
    return null
  }

  return (
    <>
      {!treatAsMemberState.isEditing && (
        <TreatAsMemberReadOnly treatAsMemberState={treatAsMemberState} treatAsMemberActions={treatAsMemberActions} />
      )}
      {treatAsMemberState.isEditing && (
        <TreatAsMemberEdit treatAsMemberState={treatAsMemberState} treatAsMemberActions={treatAsMemberActions} />
      )}
    </>
  )
}

function TreatAsMemberReadOnly({ treatAsMemberState, treatAsMemberActions }) {
  const { t } = useTranslation()

  return (
    <DmcRow>
      <div>
        <Row>
          <DataBlock inline heading="" data={`${t('treatAsMember.treatAsMember', 'Treat as Member')}:`} />
          <ActionChip label={treatAsMemberState.treatAsMember} onClick={() => treatAsMemberActions.toggleIsEditing()} />
        </Row>
      </div>
      {treatAsMemberState.responseUpdaterCisId != null && (
        <div>
          <DataBlock
            inline
            heading={`${t('treatAsMember.lastUpdater', 'Last Updater')}:`}
            data={
              <UserLink
                userId={treatAsMemberState.responseUpdaterCisId}
                userName={treatAsMemberState.responseUpdaterName}
                showId
              />
            }
          />
        </div>
      )}
      {treatAsMemberState.responseReason != null && (
        <div>
          <DataBlock inline heading={`${t('reason', 'Reason')}:`} data={treatAsMemberState.responseReason} />
        </div>
      )}
    </DmcRow>
  )
}

function TreatAsMemberEdit({ treatAsMemberState, treatAsMemberActions }) {
  const { t } = useTranslation()

  return (
    <DmcRow>
      <div>
        <RadioGroup label={t('treatAsMember.selectTreatAsMember', 'Select Treat as Member')} orientation="horizontal">
          <Radio
            label={t('yes', 'Yes')}
            name={radioButtons.YES}
            value={radioButtons.YES}
            onChange={(event) => treatAsMemberActions.setTreatAsMember(event.target.value)}
            checked={treatAsMemberState.treatAsMember === radioButtons.YES}
          />
          <Radio
            label={t('no', 'No')}
            name={radioButtons.NO}
            value={radioButtons.NO}
            onChange={(event) => treatAsMemberActions.setTreatAsMember(event.target.value)}
            checked={treatAsMemberState.treatAsMember === radioButtons.NO}
          />
        </RadioGroup>
      </div>
      {treatAsMemberState.treatAsMember === radioButtons.YES && (
        <div>
          <TextField
            label={t('reason', 'Reason')}
            value={treatAsMemberState.reason}
            onChange={(event) => treatAsMemberActions.setReason(event.target.value)}
          />
        </div>
      )}
      <div>
        <InputGroup label={'\xa0'}>
          <Button disabled={!treatAsMemberActions.isSaveButtonReady()} onClick={() => treatAsMemberActions.save()}>
            {t('action.save', 'Save')}
          </Button>
          <Button onClick={() => treatAsMemberActions.cancel()}>{t('action.cancel', 'Cancel')}</Button>
        </InputGroup>
      </div>
    </DmcRow>
  )
}

// Controller

export function updateStateToggleIsEditing(newState) {
  newState.isEditing = !newState.isEditing
}

export function updateStateSetReason(newState, payload) {
  newState.reason = payload.reason
}

export function updateStateSetTreatAsMember(newState, payload) {
  newState.treatAsMember = payload.treatAsMember
}

export function updateStateSetResponseTreatAsMember(newState, payload) {
  newState.treatAsMember = payload.responseTreatAsMember
  newState.responseTreatAsMember = payload.responseTreatAsMember
  newState.responseReason = payload.responseReason
  newState.responseUpdaterCisId = payload.responseUpdaterCisId
  newState.responseUpdaterName = payload.responseUpdaterName
}

export function updateStateClearState(newState) {
  newState.isEditing = false
  newState.reason = ''
  newState.treatAsMember = newState.responseTreatAsMember
}

export function useTreatAsMemberController(cisId, isAuthorized) {
  const notifications = useNotifications()

  const initialState = {
    isEditing: false,
    cisId,
    reason: '',
    treatAsMember: null,
    responseTreatAsMember: null,
    responseReason: null,
    responseUpdaterCisId: null,
    responseUpdaterName: null,
  }

  function reducer(oldstate, action) {
    const newState = deepCopy(oldstate)
    action.callback(newState, action.payload)
    return newState
  }

  const [treatAsMemberState, treatAsMemberDispatch] = useReducer(reducer, initialState)

  const treatAsMemberActions = {
    treatAsMemberDispatch,
    toggleIsEditing: () => {
      treatAsMemberActions.treatAsMemberDispatch({ callback: updateStateToggleIsEditing })
    },
    setReason: (reason) => {
      treatAsMemberActions.treatAsMemberDispatch({ callback: updateStateSetReason, payload: { reason } })
    },
    loadData: async () => {
      return !isAuthorized(['ViewTreatAsMember'])
        ? null
        : getAsMember(treatAsMemberState.cisId)
            .then((data) => {
              treatAsMemberActions.treatAsMemberDispatch({
                callback: updateStateSetResponseTreatAsMember,
                payload: {
                  responseTreatAsMember: radioButtons.YES,
                  responseReason: data.reason,
                  responseUpdaterCisId: data.lastUpdaterCisId,
                  responseUpdaterName: data.lastUpdaterName,
                },
              })
              return Promise.resolve()
            })
            .catch((error) => {
              if (error?.response?.status >= 400 && error?.response?.status < 500) {
                treatAsMemberActions.treatAsMemberDispatch({
                  callback: updateStateSetResponseTreatAsMember,
                  payload: { responseTreatAsMember: radioButtons.NO },
                })
              } else {
                notifications.error(`Error trying to get As Member data. CisID: ${treatAsMemberState.cisId}`)
              }
              return Promise.resolve() // error has been handled so continue
            })
    },
    setTreatAsMember: (treatAsMember) => {
      treatAsMemberActions.treatAsMemberDispatch({ callback: updateStateSetTreatAsMember, payload: { treatAsMember } })
    },
    clearState: () => {
      treatAsMemberActions.treatAsMemberDispatch({ callback: updateStateClearState })
    },
    isSaveButtonReady: () => {
      const isDifferent = treatAsMemberState.responseTreatAsMember !== treatAsMemberState.treatAsMember
      const noCondition = treatAsMemberState.treatAsMember === radioButtons.NO
      const yesCondition = treatAsMemberState.treatAsMember === radioButtons.YES && treatAsMemberState.reason !== ''
      const isAuthorizedToSave = isAuthorized('ManageTreatAsMember')
      return isAuthorizedToSave && (isDifferent && (noCondition || yesCondition)) // eslint-disable-line prettier/prettier
    },
    cancel: () => {
      treatAsMemberActions.clearState()
    },
    save: async () => {
      if (treatAsMemberState.responseTreatAsMember === treatAsMemberState.treatAsMember) {
        notifications.info(`Treat as Member is already "${treatAsMemberState.treatAsMember}"`)
        return Promise.resolve()
      }
      if (treatAsMemberState.treatAsMember === radioButtons.YES) {
        return createAsMember(treatAsMemberState.cisId, treatAsMemberState.reason).then(() => {
          treatAsMemberActions.clearState()
          treatAsMemberActions.loadData()
          return Promise.resolve()
        })
      }
      if (treatAsMemberState.treatAsMember === radioButtons.NO) {
        return deleteAsMember(treatAsMemberState.cisId).then(() => {
          treatAsMemberActions.clearState()
          treatAsMemberActions.loadData()
          return Promise.resolve()
        })
      }
      notifications.error(`Bad State. Treat as Member flag: ${treatAsMemberState.treatAsMember}`)
      return Promise.reject()
    },
  }

  return { treatAsMemberState, treatAsMemberActions }
}
